import { Box, Typography, Chip } from '@mui/material';
import { GridColDef, DataGrid, GridInitialState, DataGridProps } from '@mui/x-data-grid';
import { Employee } from 'axiosApi/models/employee';
import { getUserEntity } from 'common/UserEntityProvider';
import DefaultActions from 'components/DataGrid/DefaultActions';
import intl from 'components/i18n/ReactIntlWrapper';
import { DEFAULT_PAGINATION } from 'hooks';
import { useRef } from 'react';
import { useNavigate } from 'react-router';

export enum EmployeeTableColumns {
    NAME = 'name',
    LASTNAME = 'lastName',
    SUPERVISOR = 'supervisor',
    PROVIDER = 'provider',
    EMAIL = 'email',
    ACTIONS = 'actions',
}

const EmployeeTable = ({ data = [], columns, loading, onPaginationModelChange, handleSortModelChange, totalRecords, paginationMode = 'client', paginationModel }: {
    data: Employee[];
    columns: EmployeeTableColumns[];
    loading?: boolean;
    onPaginationModelChange?: DataGridProps['onPaginationModelChange'];
    handleSortModelChange?: DataGridProps['onSortModelChange'];
    paginationMode?: 'client' | 'server';
    totalRecords?: number;
    paginationModel?: { page: number; pageSize: number };
}) => {
    const navigate = useNavigate();
    const entityType = getUserEntity();

    const columnDefinition = useRef<GridColDef[]>(null);

    const INITIAL_STATE: GridInitialState = {
        pagination: {
            paginationModel: {
                page: DEFAULT_PAGINATION.pageNum,
                pageSize: DEFAULT_PAGINATION.pageSize,
            },
        },
        sorting: {
            sortModel: [
                {
                    field: 'Name',
                    sort: 'asc',
                },
            ],
        },
    };

    const makeColumnDefinitions = () => {
        const _columnDefinition: GridColDef[] = [];
        columns.forEach((col) => {
            switch (col) {
                case EmployeeTableColumns.NAME:
                    _columnDefinition.push({
                        field: 'name',
                        headerName: intl.get('employeeList.table.thead.name'),
                        width: 200,
                        flex: 1,
                        valueGetter: (value, row) => `${row.name || ''} ${row.lastName || ''}`.trim(),
                        renderCell: (params) => (
                            <Box display="flex" alignItems="center" gap={1}>
                                <span>{params.row.name || ''} {params.row.lastName || ''}</span>
                                {!params.row.isActive && (
                                    <Typography fontStyle="italic" fontSize={12} color="text.secondary">({intl.get('statusSelect.inactive')})</Typography>
                                )}
                            </Box>
                        ),
                    });
                    break;
                case EmployeeTableColumns.LASTNAME:
                    _columnDefinition.push({
                        field: 'lastname',
                        headerName: intl.get('employeeList.table.thead.lastName'),
                        width: 200,
                        flex: 1,
                        renderCell: (params) => <>{params.row.lastName || ''}</>,
                    });
                    break;
                case EmployeeTableColumns.SUPERVISOR:
                    _columnDefinition.push({
                        field: 'supervisor',
                        headerName: intl.get('employeeList.table.thead.supervisor') || 'Supervisor',
                        width: 200,
                        flex: 1,
                        sortable: false,
                        renderCell: (params) => {
                            const supervisor = params.row.supervisor;
                            return supervisor ? (
                                <Chip
                                    label={`${supervisor.name} ${supervisor.lastName}`}
                                    color="primary"
                                    size="small"
                                />
                            ) : null;
                        },
                    });
                    break;
                case EmployeeTableColumns.PROVIDER:
                    _columnDefinition.push({
                        field: 'provider',
                        headerName: intl.get('employeeList.table.thead.provider'),
                        width: 200,
                        flex: 1,
                        sortable: false,
                        renderCell: (params) => {
                            const provider = params.row.provider;
                            return provider ? (
                                <Chip
                                    label={`${provider.name}`}
                                    color="primary"
                                    size="small"
                                />
                            ) : null;
                        },
                    });
                    break;
                case EmployeeTableColumns.EMAIL:
                    _columnDefinition.push({
                        field: 'email',
                        headerName: intl.get('employeeList.table.thead.email'),
                        width: 250,
                        flex: 1,
                        renderCell: (params) => <>{params.row.emailAddress || ''}</>,
                    });
                    break;
                case EmployeeTableColumns.ACTIONS:
                    _columnDefinition.push({
                        field: 'actions',
                        headerName: '',
                        width: 150,
                        renderCell: (params) => (
                            <DefaultActions
                                row={params.row}
                                entityType={entityType.entityType}
                                onView={() => navigate(`/employee/detail/${params.row.id}`)}
                                identifier="ui.employees.table.view-gate"
                            />
                        ),
                    });
                    break;
                default:
                    break;
            }
        });
        return _columnDefinition;
    };

    if (!columnDefinition.current)
        columnDefinition.current = makeColumnDefinitions();

    return (
        <DataGrid
            paginationMode={paginationMode}
            loading={loading}
            rows={data}
            columns={columnDefinition.current}
            rowCount={totalRecords}
            {...(paginationModel ? { paginationModel } : { initialState: INITIAL_STATE })}
            onPaginationModelChange={onPaginationModelChange}
            onSortModelChange={handleSortModelChange}
            getRowClassName={(params) =>
                !params.row.isActive ? 'employee-inactive' : ''
            }
            sx={{
                '& .employee-inactive': {
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                },
            }}
        />
    );
};
export default EmployeeTable;